import { createSignal, Show } from "solid-js";
import zod from "zod";

import Trans from "@repo/localization/jsx.macro";
import t from "@repo/localization/string.macro";
import TransitionHeight from "@repo/ui/animation/TransitionHeight";
import Logo from "@repo/ui/branding/Logo";
import Button from "@repo/ui/buttons/Button";
import LoadableButton from "@repo/ui/buttons/LoadableButton";
import Alert from "@repo/ui/info/Alert";
import Form from "@repo/ui/inputs/Form";
import TextInput from "@repo/ui/inputs/TextInput";
import createForm from "@repo/ui/signals/createForm";
import AppRouter from "@repo/utils-solid/AppRouter";

import AuthLayout from "~/components/auth/AuthLayout";
import { useUser } from "~/state/features/user";

export default function RequestResetPassword() {
  const [, setUser] = useUser();

  const { register, handleSubmit, submittable } = createForm({
    schema: zod.object({
      email: zod
        .string({ required_error: t`Required` })
        .email(t`Invalid email`),
    }),
  });

  const [showSuccessMessage, setShowSuccessMessage] = createSignal(false);

  return (
    <AuthLayout.Login>
      <Logo class="text:heading-3 mb-4" word icon />
      <header class="text:heading-3++ mb-3">
        <Trans>Enter your email to reset password</Trans>
      </header>
      <Form
        onSubmit={handleSubmit(async (_, { email }) => {
          await setUser.forgotPassword(email);

          LoadableButton.afterSuccess(() => {
            setShowSuccessMessage(true);
          });
        })}
      >
        <TextInput
          label={t`Email`}
          type="email"
          autocomplete="email"
          {...register("email")}
        />
        <Button
          variant="text"
          size="small"
          class="mt-3 pb-2"
          onClick={() => AppRouter.replace("/login")}
        >
           <Trans>Back to login</Trans>
        </Button>
        <TransitionHeight>
          <Show when={showSuccessMessage()}>
            <Alert type="info" class="text:mini p-2" outline>
              <Trans>
                If an account with that email exists, we've sent you an email
                with instructions to reset your password.
              </Trans>
            </Alert>
          </Show>
        </TransitionHeight>
        <div class="center-child pt-2">
          <Form.Submit
            as={Button}
            variant="primary-hard"
            size="large"
            disabled={!submittable()}
            text={{ pending: t`Sending Email...`, success: t`Email Sent` }}
          >
            <Trans>Reset Password</Trans>
          </Form.Submit>
        </div>
      </Form>
    </AuthLayout.Login>
  );
}
